import React, { FC, CSSProperties, ReactNode } from 'react';
import { Dialog, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import clsx from 'clsx';
import { withStyles } from '@mui/styles';
import { Button } from 'bs-unified-ui';
import dialogParams from './dialogParams';
import styles from './style';

interface CustomDialogProps {
  icon?: string;
  title?: string;
  description?: string | string[];
  cancelText?: string;
  okText?: string;
  classes: any;
  children?: ReactNode | ((props: any) => ReactNode);
  open: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onOk?: () => void;
  closeOnClick?: boolean;
  cancelButtonProps?: any;
  titleStyle?: CSSProperties;
  descriptionStyle?: CSSProperties;
  okButtonStyle?: CSSProperties;
  cancelButtonStyle?: CSSProperties;
  buttonTextStyle?: CSSProperties;
  [key: string]: any;
}

const CustomDialog: FC<CustomDialogProps> = ({
  icon,
  title,
  description,
  cancelText,
  okText,
  classes,
  children,
  open,
  onClose = () => {},
  onCancel = () => {},
  onOk = () => {},
  closeOnClick = true,
  cancelButtonProps = {},
  titleStyle = {},
  descriptionStyle = {},
  okButtonStyle = {},
  cancelButtonStyle = {},
  buttonTextStyle = {},
  ...props
}) => {
  const iconImage: Record<string, string> = {
    warn: '/icons/warning.svg',
    warn_red: '/icons/warning-red.svg',
    success: '/icons/success.svg',
    blue_success: '/icons/blue_success.svg',
    update: '',
    trash: '/icons/trash.svg',
    red_trash: '/icons/Group-11960.svg',
    mail: '/icons/mail.svg',
    importing: '/icons/importing.svg',
    default: '',
    file: '/icons/file-break.svg',
    redWarn: '/icons/red-warning.svg',
    loader: '/icons/feather-loader.svg',
  };

  const cancel = () => {
    if (closeOnClick) {
      onCancel();
      onClose();
    } else {
      onCancel();
    }
  };

  const ok = () => {
    if (closeOnClick) {
      onOk();
      onClose();
    } else {
      onOk();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth='xs'
      fullWidth
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          cancel();
        }
      }}
      onKeyPress={(e: React.KeyboardEvent) =>
        !cancelText && e.key === 'Enter' && ok()
      }
      {...props}
    >
      {typeof children === 'function' ? (
        children({ onClose })
      ) : (
        <div className={classes.container}>
          <div className={classes.dialogHeader}>
            <img
              src={icon ? iconImage[icon] : iconImage.default}
              alt={`${icon} icon`}
            />
            <Typography
              className={classes.title}
              style={titleStyle}
              variant='h6'
            >
              {title}
            </Typography>
          </div>

          {description && (
            <div className={clsx(classes.dialogContent)}>
              {typeof description === 'string' ? (
                <p
                  className={classes.description}
                  style={descriptionStyle}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                />
              ) : (
                Array.isArray(description) && (
                  <>
                    {description.map((d, i) =>
                      typeof d === 'string' ? (
                        <p
                          className={classes.description}
                          key={i}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(d),
                          }}
                        />
                      ) : (
                        <p className={classes.description} key={i}>
                          {d}
                        </p>
                      ),
                    )}
                  </>
                )
              )}
            </div>
          )}

          {children && <div className={classes.dialogContent}>{children}</div>}

          <div className={classes.dialogFooter}>
            {cancelText && (
              <Button
                className={classes.actionBtn}
                onClick={cancel}
                rounded
                color='third'
                style={cancelButtonStyle}
                {...cancelButtonProps}
              >
                <span style={buttonTextStyle}>{cancelText}</span>
              </Button>
            )}
            <Button
              className={`${classes.actionBtn} ${classes.okButton}`}
              onClick={ok}
              style={okButtonStyle}
              rounded
            >
              <span style={buttonTextStyle}>{okText}</span>
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export { dialogParams };
export default withStyles(styles)(CustomDialog);
